import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';

interface KeyboardShortcutsModalProps {
    open: boolean;
    onClose: () => void;
}

const SHORTCUTS = [
    { category: 'Navigation', keys: ['j', '↓'], description: 'Next Item' },
    { category: 'Navigation', keys: ['k', '↑'], description: 'Previous Item' },
    { category: 'Navigation', keys: ['Enter'], description: 'Open Details' },
    { category: 'Selection', keys: ['Space'], description: 'Toggle Selection' },
    { category: 'Selection', keys: ['Shift', 'Click'], description: 'Range Select' },
    { category: 'Selection', keys: ['Esc'], description: 'Clear Selection' },
    { category: 'Actions', keys: ['d'], description: 'Mark Done' },
    { category: 'Actions', keys: ['e'], description: 'Archive / delete (archive view: delete)' },
    { category: 'Actions', keys: ['s'], description: 'Snooze (1 Day)' },
    { category: 'Drawer', keys: ['j', 'k'], description: 'Drawer: next / previous item' },
    { category: 'Drawer', keys: ['d'], description: 'Drawer: Start (triage) or Mark complete' },
    { category: 'Drawer', keys: ['e'], description: 'Drawer: Archive / Unarchive' },
    { category: 'Drawer', keys: ['s'], description: 'Drawer: Snooze (1 Day)' },
];

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({ open, onClose }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    backgroundColor: '#0f131a', // Dark theme background
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 12,
                    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.5)',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <KeyboardIcon sx={{ color: '#00f2ff' }} />
                    <Typography variant="h6">Keyboard Shortcuts</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: 'white' } }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {SHORTCUTS.map((shortcut, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 1,
                                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                                '&:last-child': { borderBottom: 'none' }
                            }}
                        >
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {shortcut.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {shortcut.keys.map((key) => (
                                    <Paper
                                        key={key}
                                        elevation={0}
                                        sx={{
                                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                                            color: 'white',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            minWidth: 24,
                                            textAlign: 'center',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {key}
                                    </Paper>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default KeyboardShortcutsModal;
